/**
 * In-memory mock of the Learning commands (docs/LMS_CONTRACT.md sections 4 to
 * 6), so the Training pages run offline the way the Knowledge Base does.
 *
 * The store is shaped like the two DynamoDB tables: one course item and, per
 * trainer, a partition of `course#` / `lesson#` / `attempt#` / `cert#` /
 * `shadow#` / `signoff#` rows. Every read derives state the way
 * `Learning._course_view` does (contract section 5), so a UI that looks right
 * here looks right against the Lambda. Lesson content is never copied: it is
 * read from `mockKnowledgeData` through `mockLoadLesson`, the mirror of
 * `Knowledge.load_lesson`, unstripped, which is what grading needs.
 *
 * Failures resolve to `{success:false, error, errorCode, details}` rather than
 * rejecting, because the pages branch on `errorCode` (`E_LESSON_LOCKED`,
 * `E_CHECK_INCOMPLETE`) and a thrown Error would lose it.
 */

import { getUserName } from '../Utils/GeneralUtils';
import { MOCK_TRAINING_ADMIN_COMMANDS, isMockViewerEditor, mockLoadLesson } from '../Knowledge/mockKnowledgeData';
import { CERT_KINDS, COMPLETIONS, ROLES } from '../Knowledge/lessonBlock';

const NETWORK_DELAY_MS = 120;
const NOW_OVERRIDE_KEY = 'bbvr_mock_now';

export { CERT_KINDS };
const CERT_EXPIRY_WARN_DAYS = 60;
const NOT_STARTED_FLAG_DAYS = 14;
const ATTEMPTS_KEPT = 20;
const PASS_SCORE_DEFAULT = 0.8;

// Same sets as Learning.py: an expired refresher was passed once, so it still
// satisfies a prerequisite; waiting states are skipped by `nextLessonId`.
const SATISFIED = new Set(['passed', 'waived', 'expired']);
const WAITING = new Set(['passed', 'waived', 'pending_cosign', 'pending_verify', 'pending_signoff']);

const DAY_MS = 24 * 60 * 60 * 1000;

/** `localStorage.bbvr_mock_now` moves the clock, like the mock shift flow. */
const now = () => {
    try {
        const raw = window.localStorage.getItem(NOW_OVERRIDE_KEY);
        if (raw) {
            const d = /^\d+$/.test(raw) ? new Date(Number(raw)) : new Date(raw);
            if (!Number.isNaN(d.getTime())) return d;
        }
    } catch (e) {
        /* no storage */
    }
    return new Date();
};

const isoNow = () => now().toISOString();
const isoDaysAgo = (days) => new Date(now().getTime() - days * DAY_MS).toISOString();
const ymd = (d) => d.toISOString().slice(0, 10);
/** Business day is UTC-6, like `TaskBoard._today()`. */
const today = () => ymd(new Date(now().getTime() - 6 * 60 * 60 * 1000));
const ymdDaysFromNow = (days) => ymd(new Date(now().getTime() + days * DAY_MS));
const parseYmd = (value) => {
    const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const daysBetween = (fromYmd, toYmd) => {
    const a = parseYmd(fromYmd);
    const b = parseYmd(toYmd);
    return a === null || b === null ? null : Math.round((b - a) / DAY_MS);
};
const fmtDay = (value) => {
    const t = parseYmd(value);
    if (t === null) return '';
    return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};
const round2 = (n) => Math.round(n * 100) / 100;
/** Python's `round()` is half-to-even; the Lambda's `percent` is, so 2 of 16 reads 12 on both sides. */
const pyRound = (n) => {
    const floor = Math.floor(n);
    const diff = n - floor;
    if (diff > 0.5) return floor + 1;
    if (diff < 0.5) return floor;
    return floor % 2 === 0 ? floor : floor + 1;
};
const slug = (name) => String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'trainer';

const currentUser = () => {
    try {
        return getUserName() || 'Demo Operator';
    } catch (e) {
        return 'Demo Operator';
    }
};

/** A failure the dispatcher turns into a `{success:false}` envelope with its code and details. */
const fail = (message, code = null, details = null) => {
    const error = new Error(message);
    error.errorCode = code;
    error.details = details;
    return error;
};

// --- Seed --------------------------------------------------------------------

export const COURSE_ID = 'course-hero-trainer';

const seedCourse = () => ({
    id: COURSE_ID,
    title: 'Hero Trainer Certification',
    description: 'Everything a Hero Trainer needs before working a shift alone.',
    status: 'published',
    requiredFor: ['HeroTrainer', 'Manager'],
    passScore: PASS_SCORE_DEFAULT,
    modules: [
        { id: 'm0-foundations', title: 'Company Foundations & Culture', summary: 'What Black Box VR is and what we believe.', lessonIds: ['lesson-m0-foundations'], prerequisites: [], completion: 'quiz', certKind: null, refreshEveryDays: null, shadowShiftsMin: null },
        { id: 'm1-booth-hardware', title: 'Booth Overview (Hardware)', summary: 'Every component in a booth and what it does.', lessonIds: ['lesson-m1-booth-hardware', 'lesson-m1-booth-walk'], prerequisites: ['m0-foundations'], completion: 'hands_on', certKind: null, refreshEveryDays: null, shadowShiftsMin: null },
        { id: 'm2-hardware-repair', title: 'Hardware Repair & Replacement', summary: 'Wear parts, greasing and when to escalate.', lessonIds: ['lesson-m2-hardware-repair', 'lesson-m2-repair-walk'], prerequisites: ['m1-booth-hardware'], completion: 'hands_on', certKind: null, refreshEveryDays: null, shadowShiftsMin: null },
        { id: 'm3-software', title: 'Software Overview', summary: 'The tools of a shift, from Scheduler to ARC.', lessonIds: ['lesson-m3-software'], prerequisites: ['m1-booth-hardware'], completion: 'quiz', certKind: null, refreshEveryDays: null, shadowShiftsMin: null },
        { id: 'm4-facility-ops', title: 'Facility / Ops Training', summary: 'Opening, closing and the cleaning cadence.', lessonIds: ['lesson-m4-facility-ops', 'lesson-m4-open-walk', 'lesson-m4-close-walk'], prerequisites: ['m1-booth-hardware', 'm2-hardware-repair', 'm3-software'], completion: 'hands_on', certKind: null, refreshEveryDays: null, shadowShiftsMin: null },
        { id: 'm5-troubleshooting', title: 'Troubleshooting Training', summary: 'Headset, game and tracking faults.', lessonIds: ['lesson-m5-troubleshooting', 'lesson-m5-troubleshooting-walk'], prerequisites: ['m1-booth-hardware', 'm2-hardware-repair', 'm3-software'], completion: 'hands_on', certKind: null, refreshEveryDays: null, shadowShiftsMin: null },
        { id: 'm6-customer-service', title: 'Customer Service Training', summary: 'Greeting, merch, promos and complaints.', lessonIds: ['lesson-m6-customer-service'], prerequisites: ['m0-foundations'], completion: 'quiz', certKind: null, refreshEveryDays: null, shadowShiftsMin: null },
        { id: 'm7-safety', title: 'Safety & Crisis Management', summary: 'Hazards, emergencies and incident reports.', lessonIds: ['lesson-m7-safety'], prerequisites: ['m1-booth-hardware', 'm4-facility-ops'], completion: 'quiz', certKind: null, refreshEveryDays: 90, shadowShiftsMin: null },
        { id: 'm8-compliance', title: 'Required Compliance Certifications', summary: 'CPR / AED and harassment prevention.', lessonIds: ['lesson-m8-cpr-aed', 'lesson-m8-harassment'], prerequisites: [], completion: 'external_cert', certKind: null, refreshEveryDays: null, shadowShiftsMin: null },
        { id: 'm9-signoff', title: 'Shadowing & Final Competency Check-Off', summary: 'Shadow shifts and the manager sign-off.', lessonIds: ['lesson-m9-signoff'], prerequisites: ['m0-foundations', 'm1-booth-hardware', 'm2-hardware-repair', 'm3-software', 'm4-facility-ops', 'm5-troubleshooting', 'm6-customer-service', 'm7-safety', 'm8-compliance'], completion: 'signoff', certKind: null, refreshEveryDays: null, shadowShiftsMin: 3 },
    ],
    version: 1,
    createdBy: 'seed_ht_course',
    createdAt: isoDaysAgo(40),
    updatedBy: 'seed_ht_course',
    updatedAt: isoDaysAgo(40),
    seededAt: isoDaysAgo(40),
});

const courseSk = (courseId) => `course#${courseId}`;
const lessonSk = (articleId) => `lesson#${articleId}`;
const attemptSk = (articleId, at) => `attempt#${articleId}#${at}`;
const certSk = (kind) => `cert#${kind}`;
const shadowSk = (date) => `shadow#${date}`;
const signoffSk = (courseId) => `signoff#${courseId}`;

const attemptRow = (articleId, { answers, correct, total, passed, at }) => ({
    sk: attemptSk(articleId, at),
    answers, score: round2(correct / total), passed, correct, total, at,
});

/**
 * The signed-in viewer: m0 passed, m1's read lesson passed on the second try,
 * the m1 walk finished yesterday and waiting on a manager, CPR recorded but
 * not verified, harassment missing. That leaves m2 and m3 locked behind the
 * co-sign, m6 and m8 open, the course `in_progress`.
 */
const seedViewer = (trainer) => {
    const rows = [
        { sk: courseSk(COURSE_ID), status: 'in_progress', enrolledAt: isoDaysAgo(20), enrolledBy: 'auto', startedAt: isoDaysAgo(18), currentLessonId: 'lesson-m1-booth-walk', courseVersion: 1 },
        { sk: lessonSk('lesson-m0-foundations'), status: 'passed', viewedAt: isoDaysAgo(18), passedAt: isoDaysAgo(18), passedBy: 'quiz', bestScore: 1, attempts: 1, articleVersion: 1, expiresAt: '', pendingCosign: false },
        attemptRow('lesson-m0-foundations', { answers: { q1: 1, q2: 1 }, correct: 2, total: 2, passed: true, at: isoDaysAgo(18) }),
        { sk: lessonSk('lesson-m1-booth-hardware'), status: 'passed', viewedAt: isoDaysAgo(12), passedAt: isoDaysAgo(10), passedBy: 'quiz', bestScore: 1, attempts: 2, articleVersion: 1, expiresAt: '', pendingCosign: false },
        attemptRow('lesson-m1-booth-hardware', { answers: { q1: 2, q2: 1, q3: 1 }, correct: 2, total: 3, passed: false, at: isoDaysAgo(12) }),
        attemptRow('lesson-m1-booth-hardware', { answers: { q1: 2, q2: 1, q3: 0 }, correct: 3, total: 3, passed: true, at: isoDaysAgo(10) }),
        { sk: lessonSk('lesson-m1-booth-walk'), status: 'viewed', viewedAt: isoDaysAgo(4), attempts: 0, articleVersion: 1, pendingCosign: true, flowRunId: 'run-mock-m1-walk', flowFinishedAt: isoDaysAgo(1) },
        { sk: certSk('cpr_aed'), provider: 'American Red Cross', completedAt: ymdDaysFromNow(-100), expiresAt: ymdDaysFromNow(630), fileKey: `certs/${slug(trainer)}/cpr_aed/6f1c2d3e4a5b4c6d8e9f0a1b2c3d4e5f.pdf`, contentType: 'application/pdf', recordedBy: trainer, recordedAt: isoDaysAgo(3), verifiedBy: '', verifiedAt: '' },
    ];
    return rows.map((row) => ({ user_name: trainer, ...row }));
};

/** Everything passed and three shadow shifts logged: ready for sign-off, so the manager surfaces have something to do. */
const seedReadyTrainer = (trainer) => {
    const passedLessons = [
        'lesson-m0-foundations', 'lesson-m1-booth-hardware', 'lesson-m2-hardware-repair', 'lesson-m3-software',
        'lesson-m4-facility-ops', 'lesson-m5-troubleshooting', 'lesson-m6-customer-service', 'lesson-m7-safety',
    ].map((id, i) => ({
        sk: lessonSk(id), status: 'passed', viewedAt: isoDaysAgo(40 - i * 3), passedAt: isoDaysAgo(39 - i * 3), passedBy: 'quiz',
        bestScore: 1, attempts: 1, articleVersion: 1, expiresAt: id === 'lesson-m7-safety' ? isoDaysAgo(39 - i * 3 - 90) : '', pendingCosign: false,
    }));
    const walks = ['lesson-m1-booth-walk', 'lesson-m2-repair-walk', 'lesson-m4-open-walk', 'lesson-m4-close-walk', 'lesson-m5-troubleshooting-walk']
        .map((id, i) => ({
            sk: lessonSk(id), status: 'passed', viewedAt: isoDaysAgo(30 - i * 2), passedAt: isoDaysAgo(30 - i * 2), passedBy: 'manager:Jordan',
            attempts: 0, articleVersion: 1, expiresAt: '', pendingCosign: false, note: 'Watched the full walk.',
        }));
    const rows = [
        { sk: courseSk(COURSE_ID), status: 'complete', enrolledAt: isoDaysAgo(45), enrolledBy: 'auto', startedAt: isoDaysAgo(44), completedAt: isoDaysAgo(6), currentLessonId: 'lesson-m9-signoff', courseVersion: 1 },
        ...passedLessons,
        ...walks,
        { sk: certSk('cpr_aed'), provider: 'American Heart Association', completedAt: ymdDaysFromNow(-700), expiresAt: ymdDaysFromNow(30), fileKey: `certs/${slug(trainer)}/cpr_aed/1a2b3c4d5e6f4a7b8c9d0e1f2a3b4c5d.pdf`, contentType: 'application/pdf', recordedBy: trainer, recordedAt: isoDaysAgo(40), verifiedBy: 'Jordan', verifiedAt: isoDaysAgo(39) },
        { sk: certSk('harassment'), provider: 'Traliant', completedAt: ymdDaysFromNow(-30), expiresAt: '', fileKey: `certs/${slug(trainer)}/harassment/9f8e7d6c5b4a4f3e2d1c0b9a8f7e6d5c.pdf`, contentType: 'application/pdf', recordedBy: trainer, recordedAt: isoDaysAgo(30), verifiedBy: 'Jordan', verifiedAt: isoDaysAgo(29) },
        { sk: shadowSk(ymdDaysFromNow(-9)), date: ymdDaysFromNow(-9), with: 'Priya Patel', signedBy: 'Jordan', notes: '', at: isoDaysAgo(9) },
        { sk: shadowSk(ymdDaysFromNow(-7)), date: ymdDaysFromNow(-7), with: 'Priya Patel', signedBy: 'Jordan', notes: 'Ran the close alone.', at: isoDaysAgo(7) },
        { sk: shadowSk(ymdDaysFromNow(-4)), date: ymdDaysFromNow(-4), with: 'Sam (HeroTrainer)', signedBy: 'Jordan', notes: '', at: isoDaysAgo(4) },
    ];
    return rows.map((row) => ({ user_name: trainer, ...row }));
};

const seedIdleTrainer = (trainer) => [
    { user_name: trainer, sk: courseSk(COURSE_ID), status: 'not_started', enrolledAt: isoDaysAgo(20), enrolledBy: 'auto', courseVersion: 1 },
];

// Gym values are the names `mockAdminData.GYMS` uses, so the Admin header's
// gym filter scopes this roster the way it scopes the trainer list.
const ROSTER = [
    { trainer: 'Alex Rivera', role: 'HeroTrainer', gym: 'Arena One' },
    { trainer: 'Priya Patel', role: 'HeroTrainer', gym: 'Arena One' },
    { trainer: 'Sam (HeroTrainer)', role: 'HeroTrainer', gym: 'Arena Two' },
];

// --- Store -------------------------------------------------------------------

let store = null;

const getStore = () => {
    if (!store) {
        const viewer = currentUser();
        const others = ROSTER.filter((r) => r.trainer !== viewer);
        store = {
            courses: [seedCourse()],
            rows: [
                ...seedViewer(viewer),
                ...(others[0] ? seedReadyTrainer(others[0].trainer) : []),
                ...(others[1] ? seedIdleTrainer(others[1].trainer) : []),
            ],
            reminders: [],
        };
    }
    return store;
};

/** Test helper: drop every row and re-seed on the next call. */
export const resetMockLearning = () => {
    store = null;
};

const viewerRole = () => (isMockViewerEditor() ? 'Manager' : 'HeroTrainer');
const isEditor = () => isMockViewerEditor();

const rowsFor = (trainer) => getStore().rows.filter((r) => r.user_name === trainer);

const putRow = (trainer, sk, fields) => {
    const s = getStore();
    const row = { user_name: trainer, sk, ...fields };
    const idx = s.rows.findIndex((r) => r.user_name === trainer && r.sk === sk);
    if (idx === -1) s.rows.push(row);
    else s.rows[idx] = row;
    return row;
};

const deleteRows = (trainer, predicate) => {
    const s = getStore();
    s.rows = s.rows.filter((r) => !(r.user_name === trainer && predicate(r)));
};

/** Mirror of `Learning.group_rows`: one partition split by sk prefix. */
const groupRows = (rows) => {
    const grouped = { course: {}, lesson: {}, attempt: [], cert: {}, shadow: {}, signoff: {} };
    rows.forEach((row) => {
        const [prefix, ...rest] = String(row.sk).split('#');
        const key = rest.join('#');
        if (prefix === 'course') grouped.course[key] = row;
        else if (prefix === 'lesson') grouped.lesson[key] = row;
        else if (prefix === 'attempt') grouped.attempt.push(row);
        else if (prefix === 'cert') grouped.cert[key] = row;
        else if (prefix === 'shadow') grouped.shadow[key] = row;
        else if (prefix === 'signoff') grouped.signoff[key] = row;
    });
    return grouped;
};

/** Drafts are for editors only; `role` is accepted for signature parity with the Lambda. */
const loadCourses = (role, { drafts = isEditor() } = {}) => getStore().courses.filter((c) => c.status === 'published' || drafts);
const loadCourse = (courseId, role, options) => loadCourses(role, options).find((c) => c.id === courseId) || null;
const lessonIdsOf = (course) => (course.modules || []).flatMap((m) => m.lessonIds || []);

/** `{articleId: load_lesson(...)}` for every lesson the course names; articles that lost their block are skipped. */
const loadLessons = (course) => {
    const out = {};
    lessonIdsOf(course).forEach((id) => {
        const loaded = mockLoadLesson(id);
        if (loaded && loaded.lesson) out[id] = loaded;
    });
    return out;
};

const blockOf = (loaded) => loaded?.lesson || {};
const questionsOf = (loaded) => blockOf(loaded).knowledgeCheck?.questions || [];
const passScoreOf = (loaded, course) => {
    const raw = blockOf(loaded).knowledgeCheck?.passScore;
    if (raw != null && raw !== '') return Number(raw);
    if (course.passScore != null) return Number(course.passScore);
    return PASS_SCORE_DEFAULT;
};

// --- State derivation (contract section 5) -------------------------------------

const certStatus = (row, day) => {
    if (!row) return ['missing', null];
    const daysLeft = row.expiresAt ? daysBetween(day, row.expiresAt) : null;
    if (daysLeft !== null && daysLeft < 0) return ['expired', daysLeft];
    if (!row.verifiedBy) return ['unverified', daysLeft];
    if (daysLeft !== null && daysLeft <= CERT_EXPIRY_WARN_DAYS) return ['expiring', daysLeft];
    return ['current', daysLeft];
};

const certificationView = (kind, row, day) => {
    const [status, daysLeft] = certStatus(row, day);
    const r = row || {};
    return {
        kind,
        label: CERT_KINDS[kind]?.label || kind,
        provider: r.provider || '',
        completedAt: r.completedAt || '',
        expiresAt: r.expiresAt || '',
        fileKey: r.fileKey || '',
        contentType: r.contentType || '',
        recordedBy: r.recordedBy || '',
        recordedAt: r.recordedAt || '',
        verifiedBy: r.verifiedBy || '',
        verifiedAt: r.verifiedAt || '',
        daysLeft,
        status,
    };
};

const certificationsView = (grouped, day) => Object.keys(CERT_KINDS).map((kind) => certificationView(kind, grouped.cert[kind], day));

const lessonState = (kind, row, certKind, grouped, courseId, day, moduleLocked) => {
    const r = row || {};
    if (r.status === 'waived') return 'waived';
    if (kind === 'external_cert') {
        const [status] = certStatus(grouped.cert[certKind], day);
        if (status === 'expired') return 'expired';
        if (status === 'unverified') return 'pending_verify';
        if (status !== 'missing') return 'passed';
    } else if (kind === 'signoff' && grouped.signoff[courseId]) {
        return 'passed';
    }
    if (r.status === 'passed') {
        return r.expiresAt && String(r.expiresAt).slice(0, 10) < day ? 'expired' : 'passed';
    }
    if (r.pendingCosign) return 'pending_cosign';
    if (moduleLocked) return 'locked';
    return 'available';
};

const lessonView = (articleId, loaded, row, state) => {
    const block = blockOf(loaded);
    const r = row || {};
    return {
        articleId,
        title: loaded?.title || articleId,
        kind: block.kind || 'read',
        minutes: Number(block.minutes) || 0,
        questionCount: questionsOf(loaded).length,
        status: r.status || null,
        state,
        bestScore: r.bestScore != null ? r.bestScore : null,
        attempts: Number(r.attempts) || 0,
        passedAt: r.passedAt || null,
        passedBy: r.passedBy || null,
        expiresAt: r.expiresAt || null,
        pendingCosign: Boolean(r.pendingCosign),
    };
};

const isSignoffModule = (module, lessonsById) =>
    module.completion === 'signoff'
    || (module.lessonIds || []).some((id) => blockOf(lessonsById[id]).kind === 'signoff');

const moduleState = (lessonViews, lessonRows, prereqStates, signoffReady) => {
    const states = lessonViews.map((v) => v.state);
    if (states.length && states.every((s) => s === 'waived')) return 'waived';
    if (states.length && states.every((s) => s === 'passed' || s === 'waived')) return 'passed';
    if (states.includes('expired')) return 'expired';
    if (signoffReady) return 'pending_signoff';
    if (states.includes('pending_cosign')) return 'pending_cosign';
    if (states.includes('pending_verify')) return 'pending_verify';
    if (prereqStates.some((s) => !SATISFIED.has(s))) return 'locked';
    if (lessonRows.some((r) => r && (r.status || Number(r.attempts)))) return 'in_progress';
    return 'available';
};

const courseView = (course, grouped, lessonsById, day) => {
    const courseRow = grouped.course[course.id] || {};
    const modules = course.modules || [];
    const moduleStates = {};
    const moduleViews = [];
    let touched = false;
    let passedCount = 0;
    let total = 0;
    const shadowCount = Object.keys(grouped.shadow).length;
    let shadowMin = null;

    const othersSatisfied = (excludeId) => modules
        .filter((m) => m.id !== excludeId && !isSignoffModule(m, lessonsById))
        .every((m) => SATISFIED.has(moduleStates[m.id]));

    modules.forEach((module) => {
        const prereqs = (module.prerequisites || []).map(String);
        const prereqStates = prereqs.map((p) => moduleStates[p]);
        const lockedBy = modules.filter((m) => prereqs.includes(m.id) && !SATISFIED.has(moduleStates[m.id])).map((m) => m.title || m.id);
        const moduleLocked = lockedBy.length > 0;
        const signoff = isSignoffModule(module, lessonsById);
        if (signoff && module.shadowShiftsMin != null) shadowMin = Number(module.shadowShiftsMin);
        const lessonViews = [];
        const lessonRows = [];
        (module.lessonIds || []).forEach((articleId) => {
            const loaded = lessonsById[articleId];
            if (!loaded) return;
            const block = blockOf(loaded);
            const row = grouped.lesson[articleId];
            const state = lessonState(block.kind || 'read', row, block.certKind, grouped, course.id, day, moduleLocked);
            lessonViews.push(lessonView(articleId, loaded, row, state));
            lessonRows.push(row);
            if ((row && (row.status || Number(row.attempts))) || !['locked', 'available'].includes(state)) touched = true;
        });
        let signoffReady = false;
        if (signoff && !lessonViews.every((v) => v.state === 'passed' || v.state === 'waived')) {
            signoffReady = shadowCount >= (Number(module.shadowShiftsMin) || 0) && othersSatisfied(module.id);
        }
        const state = moduleState(lessonViews, lessonRows, prereqStates, signoffReady);
        lessonViews.forEach((v) => {
            if (v.kind === 'signoff' && state === 'pending_signoff') v.state = 'pending_signoff';
            else if (state === 'locked' && v.state === 'available') v.state = 'locked';
        });
        moduleStates[module.id] = state;
        total += lessonViews.length;
        passedCount += lessonViews.filter((v) => v.state === 'passed' || v.state === 'waived').length;
        moduleViews.push({
            id: module.id,
            title: module.title || module.id,
            summary: module.summary || '',
            completion: module.completion || 'quiz',
            state,
            lockedBy: state === 'locked' ? lockedBy : [],
            prerequisites: prereqs,
            refreshEveryDays: module.refreshEveryDays != null ? Number(module.refreshEveryDays) : null,
            shadowShiftsMin: module.shadowShiftsMin != null ? Number(module.shadowShiftsMin) : null,
            certKind: module.certKind || null,
            lessons: lessonViews,
        });
    });

    let status;
    if (grouped.signoff[course.id]) status = 'cleared';
    else {
        const graded = moduleViews.filter((v) => !isSignoffModule(modules.find((m) => m.id === v.id), lessonsById));
        if (graded.length && graded.every((v) => SATISFIED.has(v.state))) status = 'complete';
        else status = touched ? 'in_progress' : 'not_started';
    }

    let nextLessonId = null;
    for (const view of moduleViews) {
        if (view.state === 'locked') continue;
        const next = view.lessons.find((l) => !WAITING.has(l.state));
        if (next) {
            nextLessonId = next.articleId;
            break;
        }
    }

    return {
        courseId: course.id,
        title: course.title || course.id,
        description: course.description || '',
        status,
        enrolledAt: courseRow.enrolledAt || null,
        startedAt: courseRow.startedAt || null,
        completedAt: courseRow.completedAt || null,
        clearedAt: courseRow.clearedAt || null,
        clearedBy: courseRow.clearedBy || null,
        dueAt: courseRow.dueAt || null,
        progress: { passed: passedCount, total, percent: total ? pyRound((passedCount * 100) / total) : 0 },
        currentLessonId: courseRow.currentLessonId || null,
        nextLessonId,
        shadowShifts: shadowCount,
        shadowShiftsMin: shadowMin,
        modules: moduleViews,
    };
};

const attention = (views, certifications, lessonsByCourse, grouped, day) => {
    const out = [];
    views.forEach((view) => {
        const lessons = lessonsByCourse[view.courseId] || {};
        view.modules.forEach((module) => {
            if (module.state === 'pending_signoff') {
                out.push({ kind: 'signoff_ready', text: `${module.title} is ready for sign-off.`, articleId: null, courseId: view.courseId, kind_key: null });
            }
            module.lessons.forEach((lesson) => {
                if (lesson.state === 'expired' && lesson.kind !== 'external_cert') {
                    out.push({ kind: 'refresher_due', text: `${lesson.title} refresher is due.`, articleId: lesson.articleId, courseId: view.courseId, kind_key: null });
                } else if (lesson.state === 'pending_cosign') {
                    const finished = grouped.lesson[lesson.articleId]?.flowFinishedAt;
                    const when = finished ? ` ${fmtDay(finished)}` : '';
                    out.push({ kind: 'cosign_waiting', text: `Walk finished${when}. Waiting on a manager.`, articleId: lesson.articleId, courseId: view.courseId, kind_key: null });
                } else if (lesson.kind === 'external_cert' && module.state !== 'locked' && lesson.state === 'available') {
                    const certKind = blockOf(lessons[lesson.articleId]).certKind;
                    const label = CERT_KINDS[certKind]?.label || lesson.title;
                    out.push({ kind: 'cert_missing', text: `${label} certificate not on file.`, articleId: lesson.articleId, courseId: view.courseId, kind_key: certKind });
                }
            });
        });
        if (view.status === 'not_started' && view.enrolledAt) {
            const days = daysBetween(view.enrolledAt, day);
            if (days !== null && days >= NOT_STARTED_FLAG_DAYS) {
                out.push({ kind: 'not_started', text: `${view.title} not started yet.`, articleId: null, courseId: view.courseId, kind_key: null });
            }
        }
    });
    certifications.forEach((cert) => {
        if (cert.status === 'expired') {
            out.push({ kind: 'cert_expired', text: `${cert.label} expired ${fmtDay(cert.expiresAt)}.`, articleId: null, courseId: null, kind_key: cert.kind });
        } else if (cert.status === 'expiring') {
            out.push({ kind: 'cert_expiring', text: `${cert.label} expires ${fmtDay(cert.expiresAt)} (${cert.daysLeft} days)`, articleId: null, courseId: null, kind_key: cert.kind });
        }
    });
    return out;
};

// --- Writes --------------------------------------------------------------------

const passFields = (module, passedBy) => {
    const at = isoNow();
    const refresh = module.refreshEveryDays;
    return {
        status: 'passed',
        passedAt: at,
        passedBy,
        pendingCosign: false,
        expiresAt: refresh != null && refresh !== '' ? new Date(now().getTime() + Number(refresh) * DAY_MS).toISOString() : '',
    };
};

const stripKeys = (row) => {
    const { user_name: _u, sk: _s, ...fields } = row || {};
    return fields;
};

const recomputeCourseRow = (trainer, course, lessonsById, updates = {}) => {
    const grouped = groupRows(rowsFor(trainer));
    const view = courseView(course, grouped, lessonsById, today());
    const fields = { ...stripKeys(grouped.course[course.id]), ...updates, status: view.status, courseVersion: Number(course.version) || 1 };
    if (['complete', 'cleared'].includes(view.status) && !fields.completedAt) fields.completedAt = isoNow();
    if (!fields.enrolledAt) fields.enrolledAt = isoNow();
    if (!fields.enrolledBy) fields.enrolledBy = 'self';
    putRow(trainer, courseSk(course.id), fields);
    return courseView(course, groupRows(rowsFor(trainer)), lessonsById, today());
};

const enroll = (trainer, course, enrolledBy = 'auto') =>
    putRow(trainer, courseSk(course.id), { status: 'not_started', enrolledAt: isoNow(), enrolledBy, courseVersion: Number(course.version) || 1 });

const writeAttempt = (trainer, articleId, attempt) => {
    putRow(trainer, attemptSk(articleId, attempt.at), attempt);
    const mine = rowsFor(trainer)
        .filter((r) => r.sk.startsWith(`attempt#${articleId}#`))
        .sort((a, b) => (a.at < b.at ? -1 : 1));
    const excess = mine.slice(0, Math.max(0, mine.length - ATTEMPTS_KEPT));
    excess.forEach((r) => deleteRows(trainer, (x) => x.sk === r.sk));
};

/** (course, module, lessonsById) for a lesson; enrolled courses first so progress lands where the trainer is enrolled. */
const locate = (trainer, articleId) => {
    const grouped = groupRows(rowsFor(trainer));
    const courses = loadCourses(null, { drafts: false });
    const ordered = [...courses.filter((c) => grouped.course[c.id]), ...courses.filter((c) => !grouped.course[c.id])];
    for (const course of ordered) {
        const module = (course.modules || []).find((m) => (m.lessonIds || []).includes(articleId));
        if (module) return { course, module, lessons: loadLessons(course) };
    }
    return null;
};

const lessonInView = (view, articleId) => {
    for (const module of view.modules) {
        const lesson = module.lessons.find((l) => l.articleId === articleId);
        if (lesson) return { module, lesson };
    }
    return { module: null, lesson: null };
};

/** Roster names resolve to themselves; anything else is `E_TRAINER_UNKNOWN`, like `resolve_trainer_identity` coming back empty. */
const resolveTrainer = (name) => {
    const text = String(name || '').trim();
    if (!text) throw fail('Trainer is required.', 'E_TRAINER_UNKNOWN');
    const known = text === currentUser() || ROSTER.some((r) => r.trainer === text) || rowsFor(text).length > 0;
    if (!known) throw fail(`Unknown trainer: ${text}`, 'E_TRAINER_UNKNOWN');
    return text;
};

// Mock mode has no flag store, so the LMS is on unless a test turns it off.
let disabled = false;
/** Test helper: answer as if `LMS_ENABLED` were off. Editors are never gated, like the Lambda. */
export const setMockTrainingDisabled = (value) => {
    disabled = Boolean(value);
};
const isGated = () => disabled && !isEditor();
const requireEnabled = () => {
    if (isGated()) throw fail('Training is not turned on yet.', 'E_LMS_DISABLED');
};

const myTraining = (trainer, role, { enrollMissing = true } = {}) => {
    const courses = loadCourses(null, { drafts: false });
    let grouped = groupRows(rowsFor(trainer));
    if (enrollMissing) {
        courses.forEach((course) => {
            if (!grouped.course[course.id] && (course.requiredFor || []).includes(role)) enroll(trainer, course);
        });
    }
    grouped = groupRows(rowsFor(trainer));
    const views = [];
    const browse = [];
    const lessonsByCourse = {};
    courses.forEach((course) => {
        if (grouped.course[course.id]) {
            lessonsByCourse[course.id] = loadLessons(course);
            views.push(courseView(course, grouped, lessonsByCourse[course.id], today()));
        } else {
            browse.push({ courseId: course.id, title: course.title, description: course.description || '', moduleCount: (course.modules || []).length });
        }
    });
    const certifications = certificationsView(grouped, today());
    return { views, browse, certifications, attention: attention(views, certifications, lessonsByCourse, grouped, today()), grouped };
};

const attestLesson = (trainer, articleId, status, note, by) => {
    const found = locate(trainer, articleId);
    if (!found || !found.lessons[articleId]) throw fail('Lesson not found.', 'E_LESSON_NOT_FOUND');
    const { course, module, lessons } = found;
    const kind = blockOf(lessons[articleId]).kind || 'read';
    if (!['hands_on', 'read', 'video'].includes(kind)) throw fail('Only hands-on and read lessons can be attested.', 'E_LESSON_KIND');
    const existing = groupRows(rowsFor(trainer)).lesson[articleId];
    const fields = { ...stripKeys(existing), viewedAt: existing?.viewedAt || isoNow(), attempts: Number(existing?.attempts) || 0, articleVersion: Number(lessons[articleId].version) || 1, note: note || '' };
    if (status === 'passed') Object.assign(fields, passFields(module, `manager:${by}`));
    else Object.assign(fields, { status: 'waived', passedAt: isoNow(), passedBy: `manager:${by}`, pendingCosign: false });
    putRow(trainer, lessonSk(articleId), fields);
    const view = recomputeCourseRow(trainer, course, lessons);
    return { trainer, articleId, status, courseStatus: view.status };
};

const matrixFor = (gymId) => {
    const course = loadCourses(null, { drafts: false })[0];
    const lessonsById = loadLessons(course);
    const day = today();
    const viewer = currentUser();
    // No gym (null, '' or the header's 'All' sentinel) is the whole roster.
    const roster = [{ trainer: viewer, role: viewerRole(), gym: 'Arena One' }, ...ROSTER.filter((r) => r.trainer !== viewer)]
        .filter((r) => !gymId || String(gymId) === 'All' || String(r.gym) === String(gymId));
    const pending = { cosign: [], signoff: [], verify: [] };
    const expiring = [];
    const refreshersDue = [];
    const notStarted = [];
    const trainers = roster
        .filter((r) => (course.requiredFor || []).includes(r.role) || groupRows(rowsFor(r.trainer)).course[course.id])
        .map((r) => {
            const grouped = groupRows(rowsFor(r.trainer));
            const view = courseView(course, grouped, lessonsById, day);
            const certs = certificationsView(grouped, day);
            const modules = {};
            view.modules.forEach((m) => {
                modules[m.id] = m.state;
                if (m.state === 'pending_signoff') pending.signoff.push({ trainer: r.trainer, courseId: course.id, shadowShifts: view.shadowShifts, shadowShiftsMin: view.shadowShiftsMin });
                m.lessons.forEach((l) => {
                    if (l.state === 'pending_cosign') {
                        const row = grouped.lesson[l.articleId] || {};
                        pending.cosign.push({ trainer: r.trainer, articleId: l.articleId, lessonTitle: l.title, moduleTitle: m.title, flowRunId: row.flowRunId || '', flowFinishedAt: row.flowFinishedAt || '' });
                    }
                    if (l.state === 'expired' && l.kind !== 'external_cert') refreshersDue.push({ trainer: r.trainer, articleId: l.articleId, lessonTitle: l.title, expiresAt: l.expiresAt });
                });
            });
            certs.forEach((c) => {
                // Every recorded cert nobody has verified; an expired one is a renewal, listed under `expiring`.
                if (grouped.cert[c.kind] && !c.verifiedBy && c.status !== 'expired') pending.verify.push({ trainer: r.trainer, kind: c.kind, label: c.label, provider: c.provider, completedAt: c.completedAt, expiresAt: c.expiresAt, fileKey: c.fileKey, recordedAt: c.recordedAt });
                if (c.status === 'expiring' || c.status === 'expired') expiring.push({ trainer: r.trainer, kind: c.kind, label: c.label, expiresAt: c.expiresAt, daysLeft: c.daysLeft });
            });
            const enrolledDays = view.enrolledAt ? daysBetween(view.enrolledAt, day) : null;
            if (view.status === 'not_started' && enrolledDays !== null && enrolledDays >= NOT_STARTED_FLAG_DAYS) {
                notStarted.push({ trainer: r.trainer, enrolledAt: view.enrolledAt, days: enrolledDays });
            }
            const certifications = {};
            certs.forEach((c) => {
                certifications[c.kind] = { status: c.status, expiresAt: c.expiresAt, daysLeft: c.daysLeft };
            });
            return {
                trainer: r.trainer,
                role: r.role,
                gym: r.gym,
                courseStatus: view.status,
                enrolledAt: view.enrolledAt,
                daysSinceEnrolled: enrolledDays,
                clearedAt: view.clearedAt,
                dueAt: view.dueAt,
                modules,
                passedCount: view.progress.passed,
                total: view.progress.total,
                certifications,
                shadowShifts: view.shadowShifts,
            };
        });
    return {
        courseId: course.id,
        course: { title: course.title, modules: course.modules.map((m) => ({ id: m.id, title: m.title, completion: m.completion })) },
        trainers,
        pending,
        expiring,
        refreshersDue,
        notStarted,
        questionStats: [],
    };
};

/** The Monday of the business week `day` falls in, as YYYY-MM-DD. */
const weekMonday = (day) => {
    const d = new Date(parseYmd(day));
    d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    return ymd(d);
};

/**
 * The Monday post, composed like `LearningAdmin.post_reminders`: a line only
 * when it has something to say, refreshers grouped by lesson, `expired` for a
 * lapsed cert, `recordedAt` for Verify, and the not-started line closing with
 * `none` only when another line is present. Just the headline means nothing
 * to say.
 */
const reminderText = (matrix) => {
    const lines = [];
    const signoff = matrix.pending.signoff.map((p) => `${p.trainer} (${p.shadowShifts} shadow shifts, all modules passed)`);
    const cosign = matrix.pending.cosign.map((p) => `${p.trainer}, ${p.lessonTitle} (${fmtDay(p.flowFinishedAt)})`);
    const certs = matrix.expiring.map((c) => (c.daysLeft < 0
        ? `${c.trainer} ${c.label} expired ${fmtDay(c.expiresAt)}`
        : `${c.trainer} ${c.label} expires ${fmtDay(c.expiresAt)} (${c.daysLeft} days)`));
    const verify = matrix.pending.verify.map((v) => `${v.trainer} ${v.label.toLowerCase()} certificate (recorded ${fmtDay(v.recordedAt)})`);
    const byLesson = new Map();
    matrix.refreshersDue.forEach((r) => byLesson.set(r.lessonTitle, (byLesson.get(r.lessonTitle) || 0) + 1));
    const refreshers = [...byLesson.entries()].map(([title, n]) => `${title} for ${n} trainer${n === 1 ? '' : 's'}`);
    const idle = matrix.notStarted.map((n) => n.trainer);
    if (signoff.length) lines.push(`Sign-off ready: ${signoff.join('; ')}`);
    if (cosign.length) lines.push(`Co-sign waiting: ${cosign.join('; ')}`);
    if (certs.length) lines.push(`Certs: ${certs.join('; ')}`);
    if (verify.length) lines.push(`Verify: ${verify.join('; ')}`);
    if (refreshers.length) lines.push(`Refreshers due: ${refreshers.join('; ')}`);
    if (idle.length || lines.length) lines.push(`Not started after ${NOT_STARTED_FLAG_DAYS} days: ${idle.join(', ') || 'none'}`);
    return [`Training, week of ${fmtDay(weekMonday(today()))}`, ...lines].join('\n');
};

const CSV_QUOTE = (v) => {
    const text = String(v == null ? '' : v);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const VALID_CONTENT_TYPES = { 'application/pdf': 'pdf', 'image/jpeg': 'jpg', 'image/png': 'png' };
const FILE_KEY_RE = /^certs\/[a-z0-9-]+\/(cpr_aed|harassment)\/[0-9a-f]{32}\.(pdf|jpg|png)$/;
const uuidHex = () => Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');

// --- Handlers ------------------------------------------------------------------

// --- save_course helpers, mirrors of `LearningAdmin` -------------------------

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

/** An integer, or a string of digits, as a number; anything else is null. */
const wholeIntOrNull = (raw) => {
    if (typeof raw === 'number' && Number.isInteger(raw)) return raw;
    if (typeof raw === 'string' && /^-?\d+$/.test(raw.trim())) return Number.parseInt(raw.trim(), 10);
    return null;
};

/** 0.5..1, clamped the way a lesson's is; the default when absent or unreadable. */
const cleanPassScore = (raw) => {
    if (raw === null || raw === undefined || raw === '') return PASS_SCORE_DEFAULT;
    const n = Number(raw);
    if (!Number.isFinite(n)) return PASS_SCORE_DEFAULT;
    return Math.min(1, Math.max(0.5, n));
};

/** The module's slug: its own when it is one, else from the title, suffixed until free. */
const moduleIdFor = (raw, title, used) => {
    const incoming = String(raw || '').trim();
    if (SLUG_RE.test(incoming) && incoming.length <= 60) return incoming;
    const base = (String(title).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `module-${used.length + 1}`).slice(0, 60);
    let out = base;
    let n = 2;
    while (used.includes(out)) {
        out = `${base}-${n}`;
        n += 1;
    }
    return out;
};

/** Modules in a dependency order that keeps the author's order where it can, or null on a cycle. */
const orderModules = (modules) => {
    const remaining = [...modules];
    const ordered = [];
    const placed = new Set();
    while (remaining.length) {
        const ready = remaining.find((m) => m.prerequisites.every((x) => placed.has(x)));
        if (!ready) return null;
        ordered.push(ready);
        placed.add(ready.id);
        remaining.splice(remaining.indexOf(ready), 1);
    }
    return ordered;
};

/** The module ids on or behind a cycle, for the error details. */
const cycleMembers = (modules) => {
    const resolved = new Set();
    const resolvable = (m) => !resolved.has(m.id) && m.prerequisites.every((x) => resolved.has(x));
    for (let next = modules.filter(resolvable); next.length; next = modules.filter(resolvable)) {
        next.forEach((m) => resolved.add(m.id));
    }
    return modules.filter((m) => !resolved.has(m.id)).map((m) => m.id);
};

const handlers = {
    get_my_training: () => {
        const trainer = currentUser();
        const role = viewerRole();
        if (isGated()) {
            return { enabled: false, trainer, role, courses: [], browse: [], certifications: [], attention: [] };
        }
        const { views, browse, certifications, attention: lines } = myTraining(trainer, role);
        return { enabled: true, trainer, role, courses: views, browse, certifications, attention: lines };
    },

    get_courses: () => {
        requireEnabled();
        return {
            courses: loadCourses(viewerRole()).map((c) => ({
                id: c.id, title: c.title, description: c.description || '', status: c.status, requiredFor: [...(c.requiredFor || [])],
                moduleCount: (c.modules || []).length, lessonCount: lessonIdsOf(c).length, version: Number(c.version) || 1, updatedAt: c.updatedAt || '',
            })),
        };
    },

    get_course: ([courseId]) => {
        requireEnabled();
        const course = loadCourse(String(courseId || ''), viewerRole());
        if (!course) throw fail('Course not found.', 'E_COURSE_NOT_FOUND');
        const trainer = currentUser();
        const grouped = groupRows(rowsFor(trainer));
        const loaded = loadLessons(course);
        const mine = grouped.course[course.id] ? courseView(course, grouped, loaded, today()) : null;
        // Titles for a browser who is not enrolled; `mine` carries them for everyone else.
        const lessons = Object.fromEntries(Object.entries(loaded).map(([id, l]) => [id, {
            articleId: id,
            title: l.title || id,
            kind: blockOf(l).kind || 'read',
            minutes: Number(blockOf(l).minutes) || 0,
            questionCount: questionsOf(l).length,
        }]));
        return { course: JSON.parse(JSON.stringify(course)), mine, lessons };
    },

    start_lesson: ([articleId]) => {
        requireEnabled();
        const id = String(articleId || '');
        const trainer = currentUser();
        const found = locate(trainer, id);
        if (!found || !found.lessons[id]) throw fail('Lesson not found.', 'E_LESSON_NOT_FOUND');
        const { course, module, lessons } = found;
        const grouped = groupRows(rowsFor(trainer));
        const before = courseView(course, grouped, lessons, today());
        const { module: moduleView, lesson: lessonBefore } = lessonInView(before, id);
        if (moduleView.state === 'locked') {
            throw fail(`Unlocks after ${moduleView.lockedBy.join(', ')}.`, 'E_LESSON_LOCKED', { lockedBy: moduleView.lockedBy });
        }
        const loaded = lessons[id];
        const existing = grouped.lesson[id];
        const fields = { ...stripKeys(existing) };
        if (!fields.status) fields.status = 'viewed';
        if (!fields.viewedAt) fields.viewedAt = isoNow();
        if (fields.attempts == null) fields.attempts = 0;
        fields.articleVersion = Number(loaded.version) || 1;
        let passedNow = false;
        const kind = blockOf(loaded).kind || 'read';
        if (['read', 'video'].includes(kind) && !questionsOf(loaded).length && lessonBefore.state !== 'passed') {
            Object.assign(fields, passFields(module, 'quiz'));
            passedNow = true;
        }
        putRow(trainer, lessonSk(id), fields);
        const updates = { currentLessonId: id };
        if (!grouped.course[course.id]?.startedAt) updates.startedAt = isoNow();
        const view = recomputeCourseRow(trainer, course, lessons, updates);
        const { lesson } = lessonInView(view, id);
        return { articleId: id, status: lesson.status, state: lesson.state, courseId: course.id, nextLessonId: view.nextLessonId, passed: passedNow || lesson.state === 'passed' };
    },

    submit_knowledge_check: ([articleId, rawAnswers]) => {
        requireEnabled();
        const id = String(articleId || '');
        const answers = rawAnswers && typeof rawAnswers === 'object' ? rawAnswers : {};
        const trainer = currentUser();
        const found = locate(trainer, id);
        if (!found || !found.lessons[id]) throw fail('Lesson not found.', 'E_LESSON_NOT_FOUND');
        const { course, module, lessons } = found;
        const loaded = lessons[id];
        const questions = questionsOf(loaded);
        if (!questions.length) throw fail('This lesson has no knowledge check.', 'E_LESSON_NO_CHECK');
        const grouped = groupRows(rowsFor(trainer));
        const before = courseView(course, grouped, lessons, today());
        const { module: moduleView } = lessonInView(before, id);
        if (moduleView.state === 'locked') {
            throw fail(`Unlocks after ${moduleView.lockedBy.join(', ')}.`, 'E_LESSON_LOCKED', { lockedBy: moduleView.lockedBy });
        }
        const known = questions.map((q) => q.id);
        const unknown = Object.keys(answers).filter((qid) => !known.includes(qid));
        if (unknown.length) throw fail('Unknown question.', 'E_CHECK_UNKNOWN_QUESTION', { unknown });
        const missing = known.filter((qid) => !(qid in answers));
        if (missing.length) throw fail('Answer every question.', 'E_CHECK_INCOMPLETE', { missing });

        let correct = 0;
        const results = questions.map((q) => {
            const chosen = Number.isInteger(Number(answers[q.id])) ? Number(answers[q.id]) : null;
            const hit = chosen === Number(q.answer);
            if (hit) correct += 1;
            return { questionId: q.id, correct: hit, chosen, answer: Number(q.answer), explanation: q.explanation || '' };
        });
        const total = questions.length;
        const passScore = passScoreOf(loaded, course);
        const passed = correct / total >= passScore;
        const score = round2(correct / total);
        // Contract amendment: the answers leave the server only on a pass.
        const wire = passed ? results : results.map(({ questionId, correct: hit, chosen }) => ({ questionId, correct: hit, chosen }));
        const at = isoNow();
        const attemptAnswers = {};
        results.forEach((r) => {
            if (r.chosen !== null) attemptAnswers[r.questionId] = r.chosen;
        });
        writeAttempt(trainer, id, { answers: attemptAnswers, score, passed, correct, total, at });

        const existing = grouped.lesson[id];
        const fields = { ...stripKeys(existing) };
        if (!fields.status) fields.status = 'viewed';
        if (!fields.viewedAt) fields.viewedAt = at;
        fields.attempts = (Number(existing?.attempts) || 0) + 1;
        fields.bestScore = existing?.bestScore == null ? score : Math.max(Number(existing.bestScore), score);
        fields.articleVersion = Number(loaded.version) || 1;
        if (passed) Object.assign(fields, passFields(module, 'quiz'));
        const row = putRow(trainer, lessonSk(id), fields);
        const updates = { currentLessonId: id };
        if (!grouped.course[course.id]?.startedAt) updates.startedAt = at;
        const view = recomputeCourseRow(trainer, course, lessons, updates);
        return {
            articleId: id, score, correct, total, passed, passScore, results: wire,
            attempts: fields.attempts, bestScore: fields.bestScore, lessonStatus: row.status,
            nextLessonId: view.nextLessonId, courseStatus: view.status,
        };
    },

    get_my_certifications: () => {
        requireEnabled();
        return { certifications: certificationsView(groupRows(rowsFor(currentUser())), today()) };
    },

    get_certificate_upload_url: ([kind, contentType]) => {
        requireEnabled();
        if (!CERT_KINDS[kind]) throw fail('Unknown certificate kind.', 'E_CERT_KIND');
        const ext = VALID_CONTENT_TYPES[contentType];
        if (!ext) throw fail('Use a PDF, JPEG or PNG.', 'E_CERT_CONTENT_TYPE');
        const fileKey = `certs/${slug(currentUser())}/${kind}/${uuidHex()}.${ext}`;
        return { uploadUrl: `https://mock-training-records.local/${fileKey}?X-Amz-Signature=mock`, fileKey, contentType, expiresIn: 300 };
    },

    record_certification: ([kind, record]) => {
        requireEnabled();
        if (!CERT_KINDS[kind]) throw fail('Unknown certificate kind.', 'E_CERT_KIND');
        const r = record && typeof record === 'object' ? record : {};
        const completedAt = String(r.completedAt || '');
        if (parseYmd(completedAt) === null || completedAt > today()) throw fail('Completion date must be a past date (YYYY-MM-DD).', 'E_CERT_DATE');
        let expiresAt = String(r.expiresAt || '');
        if (expiresAt && (parseYmd(expiresAt) === null || expiresAt < completedAt)) throw fail('Expiry must be on or after completion.', 'E_CERT_DATE');
        if (!expiresAt && CERT_KINDS[kind].validDays) {
            expiresAt = ymd(new Date(parseYmd(completedAt) + CERT_KINDS[kind].validDays * DAY_MS));
        }
        const trainer = currentUser();
        const fileKey = String(r.fileKey || '');
        if (!FILE_KEY_RE.test(fileKey) || !fileKey.startsWith(`certs/${slug(trainer)}/${kind}/`)) throw fail('Certificate file is not yours.', 'E_CERT_FILE_KEY');
        const ext = fileKey.split('.').pop();
        const contentType = Object.keys(VALID_CONTENT_TYPES).find((k) => VALID_CONTENT_TYPES[k] === ext) || '';
        // Re-recording replaces the row and drops the verification: a manager
        // verified the old certificate, not this one.
        putRow(trainer, certSk(kind), { provider: String(r.provider || '').trim(), completedAt, expiresAt, fileKey, contentType, recordedBy: trainer, recordedAt: isoNow(), verifiedBy: '', verifiedAt: '' });
        return { certification: certificationView(kind, groupRows(rowsFor(trainer)).cert[kind], today()) };
    },

    // --- Training admin -------------------------------------------------------

    get_training_matrix: ([gymId] = []) => matrixFor(gymId),

    get_trainer_training: ([name]) => {
        const trainer = resolveTrainer(name);
        const role = ROSTER.find((r) => r.trainer === trainer)?.role || (trainer === currentUser() ? viewerRole() : 'HeroTrainer');
        const { views, certifications, grouped } = myTraining(trainer, role, { enrollMissing: false });
        const titles = {};
        loadCourses(null).forEach((course) => {
            Object.entries(loadLessons(course)).forEach(([id, loaded]) => {
                titles[id] = loaded.title;
            });
        });
        const attempts = [...grouped.attempt]
            .sort((a, b) => (a.at < b.at ? 1 : -1))
            .slice(0, 10)
            .map((a) => {
                const articleId = a.sk.split('#')[1];
                return { ...stripKeys(a), articleId, lessonTitle: titles[articleId] || articleId };
            });
        return {
            trainer,
            role,
            courses: views,
            certifications,
            attempts,
            shadowShifts: Object.values(grouped.shadow).map((s) => ({ date: s.date, with: s.with || '', signedBy: s.signedBy || '', notes: s.notes || '' })).sort((a, b) => (a.date < b.date ? 1 : -1)),
            signoffs: Object.entries(grouped.signoff).map(([courseId, s]) => ({ courseId, by: s.by, at: s.at, observed: [...(s.observed || [])], notes: s.notes || '' })),
        };
    },

    pass_lesson_for_trainer: ([name, articleId, note]) => attestLesson(resolveTrainer(name), String(articleId || ''), 'passed', note, currentUser()),

    waive_lesson_for_trainer: ([name, articleId, note]) => attestLesson(resolveTrainer(name), String(articleId || ''), 'waived', note, currentUser()),

    reset_lesson_progress: ([name, articleId]) => {
        const trainer = resolveTrainer(name);
        const id = String(articleId || '');
        const found = locate(trainer, id);
        if (!found) throw fail('Lesson not found.', 'E_LESSON_NOT_FOUND');
        deleteRows(trainer, (r) => r.sk === lessonSk(id) || r.sk.startsWith(`attempt#${id}#`));
        recomputeCourseRow(trainer, found.course, found.lessons);
        return { trainer, articleId: id, reset: true };
    },

    log_shadow_shift: ([name, date, details]) => {
        const trainer = resolveTrainer(name);
        const day = String(date || '');
        if (parseYmd(day) === null || day > today()) throw fail('Shadow shift date must be today or earlier (YYYY-MM-DD).', 'E_SHADOW_DATE');
        const d = details && typeof details === 'object' ? details : {};
        const shadow = putRow(trainer, shadowSk(day), { date: day, with: String(d.with || '').trim(), signedBy: currentUser(), notes: String(d.notes || '').trim(), at: isoNow() });
        loadCourses(null).forEach((course) => {
            if (groupRows(rowsFor(trainer)).course[course.id]) recomputeCourseRow(trainer, course, loadLessons(course));
        });
        const { date: day2, with: withWhom, signedBy, notes } = shadow;
        return { shadow: { date: day2, with: withWhom, signedBy, notes }, count: Object.keys(groupRows(rowsFor(trainer)).shadow).length };
    },

    sign_off_competency: ([name, courseId, details]) => {
        const trainer = resolveTrainer(name);
        const course = loadCourse(String(courseId || ''), null);
        if (!course) throw fail('Course not found.', 'E_COURSE_NOT_FOUND');
        const lessons = loadLessons(course);
        const view = courseView(course, groupRows(rowsFor(trainer)), lessons, today());
        const open = view.modules
            .filter((m) => !isSignoffModule(course.modules.find((x) => x.id === m.id), lessons) && !SATISFIED.has(m.state))
            .map((m) => m.title);
        if (open.length) throw fail(`Still open: ${open.join(', ')}.`, 'E_SIGNOFF_MODULES_OPEN', { open });
        const need = Number(view.shadowShiftsMin) || 0;
        if (view.shadowShifts < need) throw fail(`${view.shadowShifts} of ${need} shadow shifts.`, 'E_SIGNOFF_SHADOW_SHORT', { have: view.shadowShifts, need });
        const d = details && typeof details === 'object' ? details : {};
        const at = isoNow();
        const by = currentUser();
        putRow(trainer, signoffSk(course.id), { by, at, observed: Array.isArray(d.observed) ? d.observed.map(String) : [], notes: String(d.notes || '').trim() });
        recomputeCourseRow(trainer, course, lessons, { clearedAt: at, clearedBy: by });
        return { trainer, courseId: course.id, clearedAt: at, clearedBy: by };
    },

    verify_certification: ([name, kind]) => {
        const trainer = resolveTrainer(name);
        if (!CERT_KINDS[kind]) throw fail('Unknown certificate kind.', 'E_CERT_KIND');
        const existing = groupRows(rowsFor(trainer)).cert[kind];
        if (!existing) throw fail('No certificate recorded.', 'E_CERT_NOT_RECORDED');
        putRow(trainer, certSk(kind), { ...stripKeys(existing), verifiedBy: currentUser(), verifiedAt: isoNow() });
        loadCourses(null).forEach((course) => {
            if (groupRows(rowsFor(trainer)).course[course.id]) recomputeCourseRow(trainer, course, loadLessons(course));
        });
        return { trainer, certification: certificationView(kind, groupRows(rowsFor(trainer)).cert[kind], today()) };
    },

    get_certificate_view_url: ([fileKey]) => {
        if (!FILE_KEY_RE.test(String(fileKey || ''))) throw fail('Certificate file key is not valid.', 'E_CERT_FILE_KEY');
        return { viewUrl: `https://mock-training-records.local/${fileKey}?X-Amz-Signature=mock-view`, expiresIn: 600 };
    },

    save_course: ([payload]) => {
        const p = payload && typeof payload === 'object' ? payload : {};
        const s = getStore();
        const title = String(p.title || '').trim().slice(0, 120);
        if (!title) throw fail('A course needs a title.', 'E_COURSE_TITLE');
        const incomingId = String(p.id || '').trim();
        if (incomingId && !SLUG_RE.test(incomingId)) throw fail('Course id must be a slug (a-z, 0-9, dashes).', 'E_COURSE_ID');
        const id = incomingId || `course-${slug(title).slice(0, 53).replace(/-$/, '')}`;
        const requiredFor = (Array.isArray(p.requiredFor) ? p.requiredFor : []).map(String);
        const unknownRoles = requiredFor.filter((r) => !ROLES.includes(r));
        if (unknownRoles.length) throw fail('requiredFor names a role that does not exist.', 'E_COURSE_ROLE', { unknown: unknownRoles });

        // Mirror of `LearningAdmin._clean_module`: a non-slug id is regenerated from the title.
        const used = [];
        const duplicates = [];
        const modules = (Array.isArray(p.modules) ? p.modules : []).filter((m) => m && typeof m === 'object').map((m, index) => {
            const moduleTitle = String(m.title || '').trim().slice(0, 120) || `Module ${index + 1}`;
            const moduleId = moduleIdFor(m.id, moduleTitle, used);
            if (used.includes(moduleId)) duplicates.push(moduleId);
            used.push(moduleId);
            return {
                id: moduleId, title: moduleTitle, summary: String(m.summary || '').trim().slice(0, 500),
                lessonIds: (Array.isArray(m.lessonIds) ? m.lessonIds : []).map(String).filter(Boolean),
                prerequisites: (Array.isArray(m.prerequisites) ? m.prerequisites : []).map(String).filter(Boolean),
                completion: COMPLETIONS.includes(m.completion) ? m.completion : 'quiz',
                certKind: Object.keys(CERT_KINDS).includes(m.certKind) ? m.certKind : null,
                refreshEveryDays: wholeIntOrNull(m.refreshEveryDays) || null,
                shadowShiftsMin: wholeIntOrNull(m.shadowShiftsMin),
            };
        });
        if (duplicates.length) throw fail('Module ids must be unique.', 'E_COURSE_MODULE_DUPLICATE', { duplicate: [...new Set(duplicates)].sort() });
        const empty = modules.find((m) => m.lessonIds.length === 0);
        if (empty) throw fail('Every module needs at least one lesson.', 'E_COURSE_MODULE_EMPTY', { module: empty.id });
        const unknownPrereqs = [...new Set(modules.flatMap((m) => m.prerequisites.filter((x) => !used.includes(x) || x === m.id)))].sort();
        if (unknownPrereqs.length) throw fail('A prerequisite must name another module in this course.', 'E_COURSE_PREREQ_UNKNOWN', { unknown: unknownPrereqs });
        const ordered = orderModules(modules);
        if (!ordered) throw fail('Prerequisites loop back on themselves.', 'E_COURSE_PREREQ_CYCLE', { cycle: cycleMembers(modules) });
        const missing = [...new Set(ordered.flatMap((m) => m.lessonIds))].filter((lid) => !mockLoadLesson(lid));
        if (missing.length) throw fail('Some lessons are not Knowledge articles with a lesson block.', 'E_COURSE_LESSON_MISSING', { missing });

        const existing = s.courses.find((c) => c.id === id);
        const item = {
            ...(existing || { createdBy: currentUser(), createdAt: isoNow() }),
            id,
            title,
            description: String(p.description || '').trim().slice(0, 2000),
            status: p.status === 'draft' ? 'draft' : 'published',
            requiredFor,
            passScore: cleanPassScore(p.passScore),
            modules: ordered,
            version: (Number(existing?.version) || 0) + 1,
            updatedBy: currentUser(),
            updatedAt: isoNow(),
        };
        if (existing) s.courses[s.courses.indexOf(existing)] = item;
        else s.courses.push(item);
        return { course: JSON.parse(JSON.stringify(item)) };
    },

    delete_course: ([courseId]) => {
        const s = getStore();
        const id = String(courseId || '');
        if (!s.courses.some((c) => c.id === id)) throw fail('Course not found.', 'E_COURSE_NOT_FOUND');
        s.courses = s.courses.filter((c) => c.id !== id);
        return { deleted: id };
    },

    set_training_due: ([name, courseId, dueAt]) => {
        const trainer = resolveTrainer(name);
        const course = loadCourse(String(courseId || ''), null);
        if (!course) throw fail('Course not found.', 'E_COURSE_NOT_FOUND');
        const due = String(dueAt || '');
        if (due && parseYmd(due) === null) throw fail('Due date must be YYYY-MM-DD.');
        const existing = groupRows(rowsFor(trainer)).course[course.id];
        if (!existing) enroll(trainer, course, currentUser());
        recomputeCourseRow(trainer, course, loadLessons(course), { dueAt: due });
        return { trainer, courseId: course.id, dueAt: due };
    },

    // Same columns as `LearningAdmin.export_training_matrix`: one per cert kind
    // (`<label> expires` when it lapses, else the label), the status where a kind
    // is missing, CRLF rows.
    export_training_matrix: ([gymId] = []) => {
        const matrix = matrixFor(gymId);
        const kinds = Object.entries(CERT_KINDS);
        const header = [
            'Trainer', 'Role', 'Status', 'Cleared',
            ...matrix.course.modules.map((m) => m.title),
            ...kinds.map(([, k]) => (k.validDays ? `${k.label} expires` : k.label)),
            'Shadow shifts',
        ];
        const certCell = (t, kind, k) => {
            const cert = t.certifications[kind] || { status: 'missing' };
            if (cert.status === 'missing' || !k.validDays) return cert.status;
            return cert.expiresAt || cert.status;
        };
        const rows = matrix.trainers.map((t) => [
            t.trainer, t.role, t.courseStatus, t.clearedAt ? t.clearedAt.slice(0, 10) : '',
            ...matrix.course.modules.map((m) => t.modules[m.id] || ''),
            ...kinds.map(([kind, k]) => certCell(t, kind, k)),
            t.shadowShifts,
        ]);
        return {
            csv: [header, ...rows].map((r) => r.map(CSV_QUOTE).join(',')).join('\r\n'),
            filename: `training-${today()}.csv`,
            count: rows.length,
            courseId: matrix.courseId,
        };
    },

    run_training_reminders: () => {
        const s = getStore();
        const text = reminderText(matrixFor(null));
        // One post a week under a claim keyed on the Monday, like the Lambda's claim row.
        const week = weekMonday(today());
        if (s.reminders.some((r) => r.week === week)) return { posted: false, text, reason: 'claimed' };
        s.reminders.push({ at: isoNow(), week, text });
        if (!text.includes('\n')) return { posted: false, text, reason: 'nothing_to_say' };
        return { posted: true, text };
    },
};

/**
 * Drop-in for `executeCommand`. Success resolves `{success:true, data}`; a
 * handler failure resolves `{success:false, error, errorCode, details}`, the
 * Lambda's `Envelope.err` shape, so `trainingUtils.run` types the error.
 */
export const mockLearningExecuteCommand = (command, args = []) =>
    new Promise((resolve) => {
        setTimeout(() => {
            const handler = handlers[command];
            if (!handler) {
                resolve({ success: false, error: `Mock: unknown command "${command}".`, errorCode: null, details: null });
                return;
            }
            // One refusal code for "the role cannot run this", like the Lambda's `E_FORBIDDEN`.
            if (MOCK_TRAINING_ADMIN_COMMANDS.includes(command) && !isMockViewerEditor()) {
                resolve({ success: false, error: 'Manager or admin access is required.', errorCode: 'E_FORBIDDEN', details: null });
                return;
            }
            try {
                resolve({ success: true, data: handler(args || []) });
            } catch (e) {
                resolve({ success: false, error: e.message || 'Request did not go through.', errorCode: e.errorCode || null, details: e.details || null });
            }
        }, NETWORK_DELAY_MS);
    });

export const MOCK_LEARNING_COMMANDS = Object.keys(handlers);
